import React, { useEffect, useMemo, useRef, useState } from "react";
import { authFetch } from "../../utils/authFetch";

const STATE_ABBREVIATIONS = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
  "DISTRICT OF COLUMBIA": "DC",
};

const normalizeStateCode = (value) => {
  const cleanValue = String(value || "").trim().toUpperCase();

  if (!cleanValue) return "";
  if (cleanValue.length === 2) return cleanValue;

  return STATE_ABBREVIATIONS[cleanValue] || cleanValue;
};

const normalizeCountyName = (value) => {
  return String(value || "")
    .trim()
    .replace(/\s+County$/i, "")
    .trim();
};

const AddressVerificationFields = ({
  values,
  onChange,
  streetLabel = "Street Address",
  showUnit = true,
  showCounty = true,
  fieldMap = {
    street: "address",
    unit: "unit",
    city: "city",
    state: "state",
    zip: "zip",
    county: "county",
    addressVerified: "address_verified",
  },
}) => {
  const resolvedFieldMap = {
    street: fieldMap.street || "address",
    unit: fieldMap.unit || "unit",
    city: fieldMap.city || "city",
    state: fieldMap.state || "state",
    zip: fieldMap.zip || "zip",
    county: fieldMap.county || "county",
    addressVerified: fieldMap.addressVerified || "address_verified",
  };

  const [message, setMessage] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);

  const lastVerifiedAddressRef = useRef("");
  const suggestionBoxRef = useRef(null);

  const getValue = (key) => values?.[resolvedFieldMap[key]] || "";

  const street = getValue("street");
  const unit = getValue("unit");
  const city = getValue("city");
  const state = normalizeStateCode(getValue("state"));
  const zip = getValue("zip");
  const county = normalizeCountyName(getValue("county"));

  const completeAddressKey = useMemo(() => {
    return [street, city, state, zip]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join("|")
      .toLowerCase();
  }, [street, city, state, zip]);

  const updateField = (key, value) => {
    const fieldName = resolvedFieldMap[key];
    if (!fieldName) return;

    if (key === "state") {
      onChange(fieldName, normalizeStateCode(value));
      return;
    }

    if (key === "county") {
      onChange(fieldName, normalizeCountyName(value));
      return;
    }

    onChange(fieldName, value);
  };

  const markUnverified = () => {
    if (resolvedFieldMap.addressVerified) {
      updateField("addressVerified", 0);
    }
  };

  const handleManualChange = (key, value) => {
    updateField(key, value);

    if (key !== "county" && key !== "unit") {
      markUnverified();

      if (["street", "city", "state", "zip"].includes(key)) {
        updateField("county", "");
      }
    }

    setMessage("");
  };

  const verifyAddress = async ({ street, city, state, zip }) => {
    const addressStreet = String(street || "").trim();
    const addressCity = String(city || "").trim();
    const addressState = normalizeStateCode(state);
    const addressZip = String(zip || "").trim();

    if (!addressStreet || !addressCity || !addressState || !addressZip) return;

    const verifyKey = [addressStreet, addressCity, addressState, addressZip]
      .join("|")
      .toLowerCase();

    if (lastVerifiedAddressRef.current === verifyKey) return;

    try {
      setVerifying(true);
      setMessage("");

      const res = await authFetch("/api/address/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          street: addressStreet,
          city: addressCity,
          state: addressState,
          zip: addressZip,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || "County could not be detected from this address.");
      }

      const verifiedCounty = normalizeCountyName(data.county || "");

      updateField("street", data.street || addressStreet);
      updateField("city", data.city || addressCity);
      updateField("state", data.state || addressState);
      updateField("zip", data.zip || addressZip);
      updateField("county", verifiedCounty);
      updateField("addressVerified", data.address_verified ? 1 : 0);

      lastVerifiedAddressRef.current = verifyKey;

      if (verifiedCounty) {
        setMessage(`${verifiedCounty} County`);
      }
    } catch (err) {
      console.error("Address verification error:", err);
      updateField("addressVerified", 0);
      setMessage("");
    } finally {
      setVerifying(false);
    }
  };

  const fetchSuggestions = async (query) => {
    const cleanQuery = String(query || "").trim();

    if (cleanQuery.length < 4) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setSearching(true);

      const res = await authFetch(
        `/api/address/suggest?q=${encodeURIComponent(cleanQuery)}`
      );

      const data = await res.json().catch(() => []);

      if (!res.ok) {
        console.error("Address suggestion error:", data);
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      const parsed = Array.isArray(data)
        ? data.map((item) => ({
            ...item,
            state: normalizeStateCode(item.state),
            county: normalizeCountyName(item.county),
          }))
        : [];

      setSuggestions(parsed);
      setShowSuggestions(parsed.length > 0);
    } catch (err) {
      console.error("Address suggestion fetch error:", err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearching(false);
    }
  };

  const handleStreetChange = (value) => {
    handleManualChange("street", value);
    fetchSuggestions(value);
  };

  const handleSuggestionSelect = (suggestion) => {
    const selectedAddress = {
      street: suggestion.street || "",
      city: suggestion.city || "",
      state: normalizeStateCode(suggestion.state),
      zip: suggestion.zip || "",
    };

    updateField("street", selectedAddress.street);
    updateField("city", selectedAddress.city);
    updateField("state", selectedAddress.state);
    updateField("zip", selectedAddress.zip);
    updateField("county", "");
    updateField("addressVerified", 0);

    setSuggestions([]);
    setShowSuggestions(false);

    verifyAddress(selectedAddress);
  };

  useEffect(() => {
    if (!street || !city || !state || !zip) return;

    const timeout = setTimeout(() => {
      verifyAddress({
        street,
        city,
        state,
        zip,
      });
    }, 900);

    return () => clearTimeout(timeout);
  }, [completeAddressKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionBoxRef.current &&
        !suggestionBoxRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div
        className="sales-field sales-field-full"
        style={{ position: "relative" }}
        ref={suggestionBoxRef}
      >
        <label>{streetLabel}</label>
        <input
          type="text"
          name={resolvedFieldMap.street}
          value={street}
          onChange={(e) => handleStreetChange(e.target.value)}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          autoComplete="off"
          required
        />

        {showSuggestions && (
          <div
            style={{
              position: "absolute",
              zIndex: 9999,
              top: "100%",
              left: 0,
              right: 0,
              background: "#ffffff",
              border: "1px solid #dbe4f0",
              borderRadius: "12px",
              boxShadow: "0 16px 32px rgba(15, 23, 42, 0.14)",
              marginTop: "6px",
              overflow: "hidden",
            }}
          >
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.label}
                type="button"
                onClick={() => handleSuggestionSelect(suggestion)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#ffffff",
                  border: "none",
                  borderBottom: "1px solid #edf2f7",
                  padding: "11px 13px",
                  cursor: "pointer",
                  color: "#0f172a",
                  fontSize: "14px",
                  lineHeight: 1.35,
                }}
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        )}

        {searching && (
          <div style={{ marginTop: "6px", color: "#64748b", fontSize: "12px" }}>
            Searching...
          </div>
        )}
      </div>

      {showUnit && (
        <div className="sales-field">
          <label>Unit</label>
          <input
            type="text"
            name={resolvedFieldMap.unit}
            value={unit}
            onChange={(e) => handleManualChange("unit", e.target.value)}
            autoComplete="address-line2"
          />
        </div>
      )}

      <div className="sales-field">
        <label>City</label>
        <input
          type="text"
          name={resolvedFieldMap.city}
          value={city}
          onChange={(e) => handleManualChange("city", e.target.value)}
          autoComplete="address-level2"
          required
        />
      </div>

      <div className="sales-field">
        <label>State</label>
        <input
          type="text"
          name={resolvedFieldMap.state}
          value={state}
          onChange={(e) => handleManualChange("state", e.target.value)}
          autoComplete="address-level1"
          maxLength={20}
          required
        />
      </div>

      <div className="sales-field">
        <label>Zip Code</label>
        <input
          type="text"
          name={resolvedFieldMap.zip}
          value={zip}
          onChange={(e) => handleManualChange("zip", e.target.value)}
          autoComplete="postal-code"
          required
        />
      </div>

      {showCounty && (
        <div className="sales-field">
          <label>County</label>
          <input
            type="text"
            name={resolvedFieldMap.county}
            value={county}
            onChange={(e) => handleManualChange("county", e.target.value)}
            placeholder={verifying ? "Detecting county..." : ""}
          />

          {message && (
            <div
              style={{
                marginTop: "6px",
                color: "#64748b",
                fontSize: "12px",
                lineHeight: 1.4,
              }}
            >
              {message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AddressVerificationFields;